import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { ErrorMessage } from "@/components/ui/error";
import { StepperProvider } from "@/components/ui/stepper";
import { Payments } from "@/features/payments";
import { PaymentStepper } from "@/features/payments/components/payment-stepper";

export const Route = createFileRoute(
  "/_authenticated/$company/unternehmen/_fullscreen/bezahlen/",
)({
  component: PaymentsPage,
  validateSearch: (search: Record<string, unknown>) => {
    // Handle both array and single value for invoiceIds
    const rawInvoiceIds = search.invoiceIds;
    const invoiceIds = Array.isArray(rawInvoiceIds)
      ? rawInvoiceIds
      : rawInvoiceIds
        ? [rawInvoiceIds as string]
        : [];

    return {
      invoiceIds: invoiceIds as string[],
      navigateBackTo: (search.navigateBackTo as string) ?? "",
    };
  },
});

function PaymentsPage() {
  const { company } = useParams({ from: Route.id });
  const { invoiceIds, navigateBackTo } = useSearch({ from: Route.id });
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!invoiceIds || invoiceIds.length === 0) {
    return <ErrorMessage />;
  }

  const handleClose = () => {
    if (navigateBackTo) {
      navigate({ to: `/${company}/unternehmen/${navigateBackTo}` });
    } else {
      navigate({
        to: "/$company/unternehmen/ueberweisungen",
        params: { company },
      });
    }
  };

  return (
    <StepperProvider>
      <div className="bg-background flex-1 h-screen overscroll-none">
        <div className="flex flex-row justify-between items-center px-4 py-2">
          <h2 className="text-xl font-semibold">{t("payments.title")}</h2>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-muted-foreground gap-2"
          >
            <X />
            {t("payments.close")}
          </Button>
        </div>
        <div className="container mx-auto py-5">
          <PaymentStepper />
          <Payments invoiceIds={invoiceIds} />
        </div>
      </div>
    </StepperProvider>
  );
}
